import React, { useState, useEffect, useRef, useMemo } from 'react';

export interface ComboBoxOption {
    id: number;
    name: string;
}

export interface ComboBoxOptionGroup<T extends ComboBoxOption = ComboBoxOption> {
    label: string;
    options: T[];
}

interface ComboBoxProps<T extends ComboBoxOption = ComboBoxOption> {
    label?: string;
    value?: number;
    options: T[];
    groupedOptions?: ComboBoxOptionGroup<T>[];
    onChange: (id: number | undefined) => void;
    disabled?: boolean;
    loading?: boolean;
    error?: string;
    placeholder?: string;
    testId?: string;
    noOptionsMessage?: string;
    loadingMessage?: string;
}

function filterGroups<T extends ComboBoxOption>(
    groups: ComboBoxOptionGroup<T>[],
    query: string
): ComboBoxOptionGroup<T>[] {
    const trimmed = query.trim();
    if (!trimmed) {
        return groups;
    }
    const lower = trimmed.toLowerCase();
    return groups
        .map((group) => ({
            label: group.label,
            options: group.options.filter((option) =>
                option.name.toLowerCase().includes(lower)
            ),
        }))
        .filter((group) => group.options.length > 0);
}

const DROPDOWN_MAX_HEIGHT_PX = 220;

function getScrollParent(el: HTMLElement | null): HTMLElement | null {
    let parent = el?.parentElement ?? null;
    while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (/(auto|scroll|overlay)/.test(overflowY)) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}

export default function ComboBox<T extends ComboBoxOption = ComboBoxOption>({
    label,
    value,
    options,
    groupedOptions,
    onChange,
    disabled = false,
    loading = false,
    error,
    placeholder = "Search...",
    testId,
    noOptionsMessage = "No options found",
    loadingMessage = "Loading..."
}: ComboBoxProps<T>) {
    const allOptions = useMemo(
        () => (groupedOptions ? groupedOptions.flatMap((group) => group.options) : options),
        [groupedOptions, options]
    );
    const baseGroups = useMemo<ComboBoxOptionGroup<T>[]>(
        () => groupedOptions ?? [{ label: '', options }],
        [groupedOptions, options]
    );

    const selectedOption = allOptions.find((option) => option.id === value);
    const [inputValue, setInputValue] = useState(selectedOption?.name || "");
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const [filteredGroups, setFilteredGroups] = useState(baseGroups);
    const [activeDescendantIndex, setActiveDescendantIndex] = useState(-1);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectableOptions = useMemo(
        () => filteredGroups.flatMap((group) => group.options),
        [filteredGroups]
    );

    const updateDropdownPlacement = () => {
        const input = inputRef.current;
        if (!input) return;

        const rect = input.getBoundingClientRect();
        const scrollParent = getScrollParent(input);
        const containerRect = scrollParent?.getBoundingClientRect();
        const containerBottom = containerRect?.bottom ?? window.innerHeight;
        const containerTop = containerRect?.top ?? 0;
        const spaceBelow = containerBottom - rect.bottom;
        const spaceAbove = rect.top - containerTop;
        const needed = DROPDOWN_MAX_HEIGHT_PX + 8;

        setOpenUpward(spaceBelow < needed && spaceAbove > spaceBelow);
    };

    useEffect(() => {
        if (selectedOption) {
            setInputValue(selectedOption.name);
        } else {
            setInputValue("");
        }
    }, [value, selectedOption]);

    useEffect(() => {
        setFilteredGroups(baseGroups);
    }, [baseGroups]);

    useEffect(() => {
        if (!isOpen) {
            setOpenUpward(false);
            return;
        }
        updateDropdownPlacement();
        const scrollParent = getScrollParent(inputRef.current);
        const onReposition = () => updateDropdownPlacement();
        window.addEventListener("resize", onReposition);
        scrollParent?.addEventListener("scroll", onReposition, { passive: true });
        return () => {
            window.removeEventListener("resize", onReposition);
            scrollParent?.removeEventListener("scroll", onReposition);
        };
    }, [isOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInputValue(val);
        setIsOpen(true);
        setActiveDescendantIndex(-1);
        setFilteredGroups(filterGroups(baseGroups, val));

        if (val === "") {
            onChange(undefined);
        }
    };

    const handleFocus = () => {
        if (!disabled && !loading) {
            setIsOpen(true);
            setFilteredGroups(baseGroups);
        }
    };

    const handleSelect = (option: T) => {
        setInputValue(option.name);
        onChange(option.id);
        setIsOpen(false);
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                if (selectedOption) {
                    setInputValue(selectedOption.name);
                } else {
                    setInputValue("");
                }
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [selectedOption]);

    const dropdownId = `${testId || 'combobox'}-listbox`;
    const optionIdPrefix = `${testId || 'combobox'}-opt`;
    const showGroupHeaders = Boolean(groupedOptions);

    let optionIndex = -1;

    return (
        <div className="field combobox" ref={wrapperRef}>
            {label && <label>{label}</label>}
            <div className="input-wrap relative">
                <input
                    ref={inputRef}
                    className={`input ${error ? 'is-error' : ''}`}
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    disabled={disabled || loading}
                    placeholder={loading ? loadingMessage : placeholder}
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={dropdownId}
                    aria-activedescendant={
                        activeDescendantIndex >= 0 && selectableOptions[activeDescendantIndex]
                            ? `${optionIdPrefix}-${selectableOptions[activeDescendantIndex].id}`
                            : undefined
                    }
                    data-testid={testId}
                />

                {isOpen && !loading && (
                    <ul
                        id={dropdownId}
                        className={`dropdown-list${openUpward ? " dropdown-list--up" : ""}`}
                        role="listbox"
                    >
                        {selectableOptions.length === 0 && (
                            <li className="no-options">{noOptionsMessage}</li>
                        )}

                        {filteredGroups.map((group) => (
                            <React.Fragment key={group.label || 'default-group'}>
                                {showGroupHeaders && group.label && (
                                    <li className="option-group-header" role="presentation">
                                        {group.label}
                                    </li>
                                )}
                                {group.options.map((option) => {
                                    optionIndex += 1;
                                    const currentIndex = optionIndex;
                                    return (
                                        <li
                                            key={option.id}
                                            id={`${optionIdPrefix}-${option.id}`}
                                            className={`option ${value === option.id ? 'selected' : ''}`}
                                            role="option"
                                            aria-selected={value === option.id}
                                            onClick={() => handleSelect(option)}
                                            onMouseEnter={() => setActiveDescendantIndex(currentIndex)}
                                        >
                                            {option.name}
                                        </li>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </ul>
                )}
            </div>
            {error && <span className="error-message">{error}</span>}

            <style>{`
                .combobox .relative { position: relative; }
                .combobox .dropdown-list {
                    position: absolute;
                    top: 100%;
                    bottom: auto;
                    left: 0;
                    right: 0;
                    max-height: ${DROPDOWN_MAX_HEIGHT_PX}px;
                    overflow-y: auto;
                    background: white;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    z-index: 10;
                    margin-top: 4px;
                    margin-bottom: 0;
                    padding: 0;
                    list-style: none;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                }
                .combobox .dropdown-list.dropdown-list--up {
                    top: auto;
                    bottom: 100%;
                    margin-top: 0;
                    margin-bottom: 4px;
                    box-shadow: 0 -4px 6px rgba(0,0,0,0.1);
                }
                .combobox .option-group-header {
                    padding: 8px 12px 4px;
                    font-size: 13px;
                    font-weight: 800;
                    color: #111827;
                    cursor: default;
                    background: #fafafa;
                    border-top: 1px solid #eee;
                }
                .combobox .option-group-header:first-child {
                    border-top: none;
                }
                .combobox .option {
                    padding: 8px 12px;
                    cursor: pointer;
                    font-size: 14px;
                }
                .combobox .option:hover, .combobox .option.selected {
                    background-color: #f0f0f0;
                }
                .combobox .no-options {
                    padding: 12px;
                    color: #888;
                    text-align: center;
                }
            `}</style>
        </div>
    );
}
